// src/gestion-entites/Gestion.entitie_client.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Avatar,
  Alert,
  Tag,
  theme,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  EyeOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  StarOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useSelection } from "../SelectionContext";
import "./styles.css";
import { useAuth } from "../context/AuthContext";
import useBusinessMutationInvalidation from "../hooks/useBusinessMutationInvalidation";
import useBusinessResource from "../hooks/useBusinessResource";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import RowActionsMenu from "../components/RowActionsMenu";
import "./clients-workspace.css";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import EntityDetailsModal from "./EntityDetailsModal";
import EntityFormModal from "./EntityFormModal";

// --- Helper function to get image URL (simplified - trust backend) ---
const getImageUrl = (imagePath) => {
  // Backend normalizes all URLs - just return what we get
  return imagePath || undefined;
};

// --- Helper function to normalize status values (CRITICAL FIX) ---
// Exportés pour être réutilisés par l'écran mobile (src/mobile/entites/).
export const normalizeStatus = (status) => {
  if (!status) return "enabled"; // Default value

  // Convert to string and normalize
  const statusStr = String(status).toLowerCase().trim();

  // Map all possible values to correct technical values
  const statusMap = {
    'enabled': 'enabled',
    'disabled': 'disabled',
    'activé': 'enabled',
    'active': 'enabled',
    'actif': 'enabled',
    'désactivé': 'disabled',
    'desactive': 'disabled',
    'inactif': 'disabled',
    'inactive': 'disabled',
    '1': 'enabled',
    '0': 'disabled',
    'true': 'enabled',
    'false': 'disabled'
  };

  return statusMap[statusStr] || 'enabled'; // Default to enabled if unknown
};

// --- Validate status before sending to backend ---
export const validateStatusValue = (status) => {
  const normalized = normalizeStatus(status);
  if (!['enabled', 'disabled'].includes(normalized)) {
    console.error(`⚠️ ERREUR STATUT: Valeur non-autorisée "${status}" → normalisée vers "${normalized}"`);
    return 'enabled'; // Fallback sécurisé
  }
  return normalized;
};
// --- End Helpers ---

const toSafeNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatCurrency = (value) =>
  `${new Intl.NumberFormat("fr-FR").format(toSafeNumber(value))} CFA`;

const GestionEntitieClient = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);

  // États pour la prévisualisation de l'image
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  // États pour la suppression sécurisée
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteLoading] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedClientDetails, setSelectedClientDetails] = useState(null);

  // Accès au contexte de sélection
  const { selectedCompany, selectedWarehouse } =
    useSelection();
  const { user, hasPermission } = useAuth();
  const invalidateClients = useBusinessMutationInvalidation("customer");
  const { token } = theme.useToken();
  const dark = ["#000", "#000000"].includes(token.colorBgBase);
  const companyId = selectedCompany?.id ?? selectedCompany;
  const warehouseId = selectedWarehouse?.id ?? selectedWarehouse;
  const canView = hasPermission("Gestion Commerciale.Entites.Clients.view");
  const scope = { userId: user?.id, companyId, warehouseId };
  const customerResource = useBusinessResource({
    ...scope, resource: "customer-list", scopeLevel: "company", enabled: canView && Boolean(companyId), active: true,
    load: async (signal) => {
      const { data } = await axios.get("/api/users/customers", {
        params: { companyId: Number(companyId), ...(warehouseId ? { warehouseId: Number(warehouseId) } : {}) },
        headers: { "X-Company-Id": String(companyId) }, signal,
      });
      if (!Array.isArray(data)) throw new Error("Réponse clients invalide.");
      return data;
    },
  });
  const warehouseResource = useBusinessResource({
    ...scope, warehouseId: null, resource: "customer-warehouses", scopeLevel: "company",
    enabled: canView && Boolean(companyId), active: isModalVisible,
    load: async (signal) => {
      const { data } = await axios.get("/api/warehouses", {
        params: { company_id: companyId }, headers: { "X-Company-Id": String(companyId) }, signal,
      });
      if (!Array.isArray(data.warehouses)) throw new Error("Réponse établissements invalide.");
      return data.warehouses;
    },
  });
  const accessLost = [401, 403, 404].includes(customerResource.error?.response?.status);
  const clients = useMemo(() => accessLost ? [] : customerResource.data || [], [accessLost, customerResource.data]);
  const warehouses = warehouseResource.data || [];
  const fetchClients = () => { void customerResource.refresh().catch(() => {}); };
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // État pour les filtres (ajout d'un filtre warehouse explicite)
  const [filters, setFilters] = useState({
    search: "",
    status: "all", // Nouveau filtre de statut
  });

  useEffect(() => {
    setIsModalVisible(false);
    setDetailsModalVisible(false);
    setDeleteModalVisible(false);
    setPreviewVisible(false);
    setPage(1);
  }, [user?.id, companyId, warehouseId]);
  useEffect(() => { setPage(1); }, [filters.search, filters.status]);

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Modified handlePreview to accept URL directly or file object
  const handlePreview = async (fileOrUrl) => {
    let imageUrl = "";
    let title = "";

    if (typeof fileOrUrl === "string") {
      // If it's a URL string (likely from table render)
      imageUrl = fileOrUrl;
      title = fileOrUrl.substring(fileOrUrl.lastIndexOf("/") + 1);
    } else if (fileOrUrl && fileOrUrl.originFileObj) {
      // If it's a file object from Upload component
      if (!fileOrUrl.url && !fileOrUrl.preview) {
        fileOrUrl.preview = await getBase64(fileOrUrl.originFileObj);
      }
      imageUrl = fileOrUrl.url || fileOrUrl.preview;
      title =
        fileOrUrl.name ||
        fileOrUrl.url.substring(fileOrUrl.url.lastIndexOf("/") + 1);
    } else if (fileOrUrl && fileOrUrl.url) {
      // Handle case where it might be a file object already processed (e.g., in form state)
      imageUrl = fileOrUrl.url;
      title =
        fileOrUrl.name ||
        fileOrUrl.url.substring(fileOrUrl.url.lastIndexOf("/") + 1);
    }

    if (imageUrl) {
      setPreviewImage(imageUrl);
      setPreviewVisible(true);
      setPreviewTitle(title);
    } else {
      message.error("Impossible d'afficher l'aperçu de l'image.");
    }
  };

  const handleCancelPreview = () => setPreviewVisible(false);

  const handleAdd = () => {
    setEditingClient(null);
    form.resetFields();

    // Valeurs par défaut pour le formulaire
    const defaultValues = {
      warehouse_id: warehouseId || undefined,
      company_id: companyId || undefined,
      user_details: {
        opening_balance_type: "receive",
        opening_balance: 0,
        rccm: "",
        ifu: "",
        credit_period: 0,
        credit_limit: 0,
        loyalty_status: "active",
        loyalty_status_reason: "",
        purchase_order_count: 0,
        purchase_return_count: 0,
        sales_order_count: 0,
        sales_return_count: 0,
        total_amount: 0,
        paid_amount: 0,
        due_amount: 0,
      },
      address: "",
      shipping_address: "",
      profile_image: [],
      status: validateStatusValue("enabled"), // Utiliser la validation
    };

    form.setFieldsValue(defaultValues);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingClient(record);
    const userDetails = {
      opening_balance: record.opening_balance || 0,
      opening_balance_type: record.opening_balance_type || "receive",
      rccm: record.rccm || "",
      ifu: record.ifu || "",
      credit_period: record.credit_period || 0,
      credit_limit: record.credit_limit || 0,
      loyalty_status: record.loyalty_status || "active",
      loyalty_status_reason: record.loyalty_status_reason || "",
      purchase_order_count: record.purchase_order_count || 0,
      purchase_return_count: record.purchase_return_count || 0,
      sales_order_count: record.sales_order_count || 0,
      sales_return_count: record.sales_return_count || 0,
      total_amount: record.total_amount || 0,
      paid_amount: record.paid_amount || 0,
      due_amount: record.due_amount || 0,
    };

    // Use helper function to get absolute URL for preview
    const absoluteImageUrl = getImageUrl(record.profile_image);
    let fileList = [];
    if (absoluteImageUrl) {
      fileList = [
        {
          uid: "-1",
          name: record.profile_image.split("/").pop() || "image.png",
          status: "done",
          url: absoluteImageUrl, // Use absolute URL
          thumbUrl: absoluteImageUrl, // Use absolute URL for thumbnail
        },
      ];
    }

    form.setFieldsValue({
      ...record,
      status: validateStatusValue(record.status), // CRITIQUE: Normaliser le statut depuis la DB
      user_details: userDetails,
      profile_image: fileList, // Set the processed fileList
      warehouse_id: record.warehouse_id,
      company_id: record.company_id,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (record) => {
    setClientToDelete(record);
    setDeleteModalVisible(true);
  };

  const handleViewDetails = (record) => {
    setSelectedClientDetails(record);
    setDetailsModalVisible(true);
    void customerResource.refresh().catch(() => {});
  };

  const handleDeleteConfirm = async (action, result) => {
    try {
      let successMessage;

      switch (action) {
        case 'delete':
          successMessage = "Client supprimé avec succès";
          break;
        case 'archive':
          successMessage = "Client archivé avec succès";
          break;
        case 'disable':
          successMessage = "Client désactivé avec succès";
          break;
        default:
          successMessage = "Action exécutée avec succès";
      }

      message.success(successMessage);
      void invalidateClients();
      fetchClients();
      setDeleteModalVisible(false);
      setClientToDelete(null);
    } catch (error) {
      console.error("Erreur lors de la gestion de la suppression:", error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setClientToDelete(null);
  };

  const handleModalOk = async () => {
    try {
      if (warehouseResource.status !== "verified" || (editingClient && customerResource.status !== "verified")) {
        message.error("Actualisez les données avant d’enregistrer le client.");
        return;
      }
      const values = await form.validateFields();
      setConfirmLoading(true);

      // Vérifier si un magasin est sélectionné dans le formulaire
      if (!values.warehouse_id) {
        Modal.warning({
          title: "Établissement requis",
          content: "Veuillez sélectionner un établissement avant d'enregistrer.",
        });
        setConfirmLoading(false);
        return;
      }

      // Préparer les données du formulaire
      const formData = new FormData();

      // Ajouter les champs principaux
      Object.keys(values).forEach((key) => {
        if (key === "profile_image" && values[key]) {
          // Si c'est un tableau de fichiers (upload)
          if (Array.isArray(values[key])) {
            if (values[key].length > 0 && values[key][0].originFileObj) {
              // Nouveau fichier sélectionné
              formData.append("profile_image", values[key][0].originFileObj);
            } else if (
              values[key].length === 0 &&
              editingClient?.profile_image
            ) {
              // Si le tableau est vide et qu'il y avait une image avant, envoyer un indicateur de suppression
              formData.append("remove_profile_image", "true");
            }
          }
        } else if (key === "user_details") {
          // Traiter les détails utilisateur séparément
          const normalizedUserDetails = { ...(values.user_details || {}) };
          if (normalizedUserDetails.loyalty_status === "active") {
            normalizedUserDetails.loyalty_status_reason = "";
          }
          Object.keys(normalizedUserDetails).forEach((detailKey) => {
            if (
              normalizedUserDetails[detailKey] !== undefined &&
              normalizedUserDetails[detailKey] !== null
            ) {
              formData.append(detailKey, normalizedUserDetails[detailKey]);
            }
          });
        } else if (values[key] !== undefined && values[key] !== null) {
          // Ne jamais envoyer le champ password s'il existe dans les valeurs
          if (key !== "password" && key !== "password_confirmation") {
            // CRITIQUE: Validation spéciale pour le statut
            if (key === "status") {
              const validatedStatus = validateStatusValue(values[key]);
              console.log(`[STATUT FIX] Original: "${values[key]}" → Validé: "${validatedStatus}"`);
              formData.append(key, validatedStatus);
            } else {
              formData.append(key, values[key]);
            }
          }
        }
      });

      // Ajouter le type d'utilisateur
      formData.append("user_type", "customers");

      // Ajouter l'ID du magasin sélectionné si pas déjà défini
      if (!values.warehouse_id && selectedWarehouse) {
        const warehouseId = Number(selectedWarehouse);
        formData.append("warehouse_id", warehouseId);
        formData.append("details_warehouse_id", warehouseId);
      } else if (values.warehouse_id) {
        const warehouseId = Number(values.warehouse_id);
        formData.append("warehouse_id", warehouseId);
        formData.append("details_warehouse_id", warehouseId);
      }

      // Ajouter l'ID de l'entreprise si pas déjà défini
      if (!values.company_id && selectedCompany) {
        const companyId = Number(selectedCompany);
        formData.append("company_id", companyId);
      } else if (values.company_id) {
        const companyId = Number(values.company_id);
        formData.append("company_id", companyId);
      }

      // Ajouter les valeurs par défaut pour les champs requis par le backend
      formData.append(
        "timezone",
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );
      formData.append("date_format", "d-m-Y");
      formData.append("date_picker_format", "dd-mm-yyyy");
      formData.append("time_format", "h:i a");

      if (editingClient) {
        // Mise à jour d'un client existant
        await axios.put(`/api/users/${editingClient.id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        message.success("Client mis à jour avec succès");
      } else {
        // Création d'un nouveau client
        await axios.post("/api/users", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        message.success("Client ajouté avec succès");
      }

      // Rafraîchir la liste des clients
      void invalidateClients();
      fetchClients();
      setIsModalVisible(false);
      setEditingClient(null);
    } catch (error) {
      if (error.errorFields?.length) return;
      message.error(
        "Erreur lors de la soumission du formulaire: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  // Filtrer les clients en fonction des critères
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      // Filtre de recherche
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = !filters.search ||
        client.name?.toLowerCase().includes(searchLower) ||
        client.email?.toLowerCase().includes(searchLower) ||
        client.phone?.toLowerCase().includes(searchLower);

      // Filtre de statut
      const matchesStatus = filters.status === "all" ||
        normalizeStatus(client.status) === filters.status;

      return matchesSearch && matchesStatus;
    });
  }, [clients, filters]);

  // Statistiques calculées
  const statistics = useMemo(() => {
    const total = filteredClients.length;
    const enabled = filteredClients.filter(c => normalizeStatus(c.status) === "enabled").length;
    const disabled = filteredClients.filter(c => normalizeStatus(c.status) === "disabled").length;

    return { total, enabled, disabled };
  }, [filteredClients]);

  const columns = [
    {
      title: "Client", dataIndex: "name", key: "name", width: 280,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (name, record) => {
        const initials = (name || "").trim().split(/\s+/).slice(0, 2).map(word => word[0]).join("").toUpperCase();
        const avatar = <Avatar size={40} src={getImageUrl(record.profile_image)}>{initials || <UserOutlined />}</Avatar>;
        return <div className="clients-identity">
          {record.profile_image ? <button type="button" className="clients-logo-preview"
            aria-label={`Aperçu du logo de ${name}`} onClick={() => handlePreview(record.profile_image)}>{avatar}</button> : avatar}
          <span className="clients-name">{name || "Client sans nom"}</span>
        </div>;
      },
    },
    {
      title: "Établissement", dataIndex: "warehouse_name", key: "warehouse", width: 225,
      sorter: (a, b) => (a.warehouse_name || "").localeCompare(b.warehouse_name || ""),
      render: name => name || "Non renseigné",
    },
    {
      title: "Total ventes (CFA)", key: "financial_total_amount", width: 210, align: "right",
      sorter: (a, b) => toSafeNumber(a.financial_total_amount) - toSafeNumber(b.financial_total_amount),
      render: (_, record) => <span className="clients-amount">{formatCurrency(record.financial_total_amount)}</span>,
    },
    {
      title: "Montant dû (CFA)", key: "financial_due_amount", width: 210, align: "right",
      sorter: (a, b) => toSafeNumber(a.financial_due_amount) - toSafeNumber(b.financial_due_amount),
      render: (_, record) => <span className={`clients-amount${toSafeNumber(record.financial_due_amount) > 0 ? " clients-amount--due" : ""}`}>
        {formatCurrency(record.financial_due_amount)}
      </span>,
    },
    {
      title: "Statut", dataIndex: "status", key: "status", width: 155,
      render: status => normalizeStatus(status) === "enabled"
        ? <Tag icon={<CheckCircleOutlined />} color="success">Actif</Tag>
        : <Tag icon={<CloseCircleOutlined />}>Désactivé</Tag>,
    },
    {
      title: "Fidélité", dataIndex: "loyalty_status", key: "loyalty_status", width: 155,
      render: status => {
        const normalized = String(status || "active").toLowerCase();
        if (normalized === "revoked") return <Tag color="error">Révoquée</Tag>;
        if (normalized === "disabled") return <Tag color="warning">Désactivée</Tag>;
        return <Tag icon={<StarOutlined />} color="success">Active</Tag>;
      },
    },
    {
      title: "Actions", key: "actions", width: 72, align: "center", fixed: "right",
      render: (_, record) => {
        const items = [{ key: "view", icon: <EyeOutlined aria-hidden />, label: "Voir le détail", onClick: () => handleViewDetails(record) }];
        if (hasPermission("Gestion Commerciale.Entites.Clients.edit")) {
          items.push({ key: "edit", icon: <EditOutlined aria-hidden />, label: "Modifier",
            disabled: customerResource.status !== "verified", onClick: () => handleEdit(record) });
        }
        if (hasPermission("Gestion Commerciale.Entites.Clients.delete")) {
          items.push({ type: "divider" }, { key: "delete", icon: <DeleteOutlined aria-hidden />, label: "Supprimer", danger: true,
            disabled: customerResource.status !== "verified", onClick: () => handleDelete(record) });
        }
        return <RowActionsMenu items={items} ariaLabel={`Afficher les actions du client ${record.name || record.id}`} />;
      },
    },
  ];

  if (!canView) {
    return <div className="gestion-entitie-container"><h1>Clients</h1><p>Vous n'avez pas les permissions nécessaires pour voir cette section.</p></div>;
  }

  return (
    <div className="gestion-entitie-container clients-workspace" style={{
      "--clients-surface": token.colorBgContainer, "--clients-text": token.colorText,
      "--clients-heading": dark ? token.colorTextHeading : token.colorInfoTextActive, "--clients-muted": token.colorTextSecondary,
      "--clients-border": token.colorBorderSecondary, "--clients-fill": token.colorFillAlter,
      "--clients-avatar-text": dark ? token.colorTextHeading : token.colorInfoText,
      "--clients-danger": dark ? `color-mix(in srgb, ${token.colorErrorText} 35%, ${token.colorText} 65%)` : token.colorErrorText,
      "--clients-success": token.colorSuccessText,
      "--clients-focus": token.colorPrimaryBorder,
      "--color-primary": dark ? token.colorTextHeading : token.colorPrimary,
    }}>
      <header className="clients-workspace__header">
        <div><h1>Clients</h1><p>Retrouvez vos clients et suivez les montants dus.</p></div>
        {hasPermission("Gestion Commerciale.Entites.Clients.create") && <Button type="primary" icon={<PlusOutlined aria-hidden />}
          size="large" onClick={handleAdd} disabled={!companyId} title={!companyId ? "Sélectionnez d'abord une entreprise dans l'en-tête" : "Ajouter un nouveau client"}>
          Ajouter un client
        </Button>}
      </header>
      <div className="clients-summary" role="region" aria-label="Résumé des clients affichés">
        <div><UserOutlined /><strong>{customerResource.data && !accessLost ? statistics.total : "—"}</strong><span>clients</span></div>
        <div className="clients-summary__active"><CheckCircleOutlined /><strong>{customerResource.data && !accessLost ? statistics.enabled : "—"}</strong><span>actifs</span></div>
        <div className="clients-summary__disabled"><CloseCircleOutlined /><strong>{customerResource.data && !accessLost ? statistics.disabled : "—"}</strong><span>désactivés</span></div>
      </div>
      {!companyId && <Alert showIcon type="info" message="Sélectionnez une entreprise pour afficher ses clients." />}
      <BusinessResourceNotice resource={customerResource} label="la liste des clients" />
      <div className="clients-toolbar" role="search" aria-label="Filtrer les clients">
        <Input aria-label="Rechercher un client" placeholder="Rechercher par nom, email ou téléphone…" prefix={<SearchOutlined />}
          value={filters.search} onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))} allowClear size="large" />
        <Select aria-label="Statut des clients" value={filters.status} size="large"
          onChange={status => setFilters(prev => ({ ...prev, status }))}
          options={[{ value: "all", label: "Tous les statuts" }, { value: "enabled", label: "Actifs" }, { value: "disabled", label: "Désactivés" }]} />
        <Button size="large" icon={<ReloadOutlined aria-hidden />} onClick={() => { setFilters({ search: "", status: "all" }); setPage(1); }}>Réinitialiser</Button>
      </div>
      <Table className="clients-table" columns={columns} dataSource={filteredClients} rowKey="id"
        loading={customerResource.loading} size="middle"
        pagination={{ current: page, pageSize, showSizeChanger: true, pageSizeOptions: [5, 10, 20, 50],
          onChange: (nextPage, nextSize) => { setPage(nextSize === pageSize ? nextPage : 1); setPageSize(nextSize); },
          showTotal: (total, range) => `${range[0]}–${range[1]} sur ${total} clients`, }}
        locale={{ emptyText: customerResource.error ? "La liste des clients n’est pas disponible. Réessayez." : !companyId ? "Sélectionnez une entreprise" : "Aucun client trouvé" }}
        scroll={{ x: 1120 }} />
      <EntityFormModal open={isModalVisible} entityType="customer" editing={Boolean(editingClient)}
        form={form} onSubmit={handleModalOk} onCancel={() => setIsModalVisible(false)} confirmLoading={confirmLoading}
        warehouses={warehouses} warehouseId={warehouseId} warehouseResource={warehouseResource}
        entityResource={customerResource} onPreview={handlePreview} />
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={handleCancelPreview}
      >
        <img alt="Preview" style={{ width: "100%" }} src={previewImage} />
      </Modal>

      {/* Modal de suppression sécurisée */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        user={clientToDelete}
        userType="client"
        loading={deleteLoading}
      />
      <EntityDetailsModal
        visible={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        entity={clients.find(record => String(record.id) === String(selectedClientDetails?.id)) || null}
        resource={customerResource}
        entityType="customer"
      />
    </div>
  );
};

export default GestionEntitieClient;
